import type { FleetFileNode } from '../api/client'
import { StagedBadge } from '../components/StagedBadge'

/**
 * The files that live inside one skill, and the affordance to remove one.
 *
 * WHY THIS EXISTS. Skill Studio could edit exactly one file — SKILL.md — and the
 * Files tab's propose refuses `op: delete` by name, because removing a whole
 * artifact has a reference cascade to compute and its own lane. Neither covers
 * the ordinary case of dropping a reference file from a skill that stays. An
 * operator who asked for it in the fleet chat got the change made by a delegate
 * with raw git access, which no recorder watches: no Improvement row was written
 * and the pull request was merged by hand on GitHub. The affordance is what puts
 * that request back inside the review flow.
 *
 * SKILL.md is listed but never deletable here. Deleting it would empty the skill,
 * which is the whole-artifact lane's job.
 *
 * The dangling-reference warning here is ADVISORY on purpose. Removing a file and
 * the line that points at it is ONE change, so the operator is routinely doing
 * both at once; blocking on THIS scan would refuse the correct case. It reads the
 * text it is GIVEN — the live draft while editing — for the same reason.
 *
 * Since 2026-09-09 the SERVER also refuses, and that refusal is authoritative
 * (ADR-123 D3/D5, ProposeSkill). It does not inherit the objection above because
 * it scans the RESULT of the change set rather than the current text, so the
 * delete-and-rewrite case passes it. It also sees the whole fleet, which this
 * scan cannot: a sibling skill naming the file is invisible here and is exactly
 * what went undetected on spartan. Treat this as the early hint and the server
 * as the gate; the panel that renders the refusal is DeleteBlockedPanel.
 */

export interface SkillFilesProps {
  agent: string
  slug: string
  /** Every file node in the fleet tree. Null while it is still being read —
   *  which is not the same as a skill with no files, and must not render as it. */
  tree: FleetFileNode[] | null
  /** Paths already approved and awaiting deploy (ADR-111 D2). Badged, never blocked. */
  staged: Set<string>
  /** Paths currently marked for deletion in this changeset. */
  marked: string[]
  onChange: (paths: string[]) => void
  /** The SKILL.md text to scan for references to a file being removed. */
  skillText: string
  /** False for a viewer, or a core-owned skill: the panel still lists the files. */
  canDelete: boolean
  disabled?: boolean
}

/** One line of skillText that still mentions a path being removed. */
interface Dangling {
  path: string
  line: number
  text: string
}

/**
 * Lines in skillText that reference a marked path, by its skill-relative path or
 * by its bare basename. Both forms appear in practice — a SKILL.md links
 * `references/thresholds.md` and prose says "see thresholds.md" — and reporting
 * only one of them would make the warning look reliable while missing half.
 */
export function danglingReferences(skillText: string, dir: string, marked: string[]): Dangling[] {
  const lines = skillText.split('\n')
  const out: Dangling[] = []
  for (const path of marked) {
    const relative = path.startsWith(dir) ? path.slice(dir.length) : path
    const basename = relative.split('/').pop() ?? relative
    const i = lines.findIndex(l => l.includes(relative) || l.includes(basename))
    if (i >= 0) out.push({ path: relative, line: i + 1, text: lines[i].trim() })
  }
  return out
}

export function SkillFiles({
  agent,
  slug,
  tree,
  staged,
  marked,
  onChange,
  skillText,
  canDelete,
  disabled,
}: SkillFilesProps) {
  const dir = `agents/${agent}/skills/${slug}/`
  const skillMd = dir + 'SKILL.md'

  if (tree === null) {
    return (
      <div data-testid="skill-files-loading" className="rounded border border-gray-200 bg-gray-50/60 px-3 py-2 text-xs text-gray-500">
        Reading this skill&apos;s files…
      </div>
    )
  }

  const files = tree
    .filter(f => f.path.startsWith(dir))
    .sort((a, b) => a.path.localeCompare(b.path))
  const others = files.filter(f => f.path !== skillMd)
  const markedSet = new Set(marked)
  const dangling = danglingReferences(skillText, dir, marked)

  const toggle = (path: string) => {
    onChange(markedSet.has(path) ? marked.filter(p => p !== path) : [...marked, path])
  }

  return (
    <div className="rounded border border-gray-200 bg-gray-50/60 px-3 py-2 text-xs space-y-2">
      <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Files in this skill</div>

      {others.length === 0 && (
        <p className="text-gray-500">This skill has no other files — just SKILL.md.</p>
      )}

      <ul className="space-y-1">
        {files.map(f => {
          const relative = f.path.slice(dir.length)
          const isSkillMd = f.path === skillMd
          const isMarked = markedSet.has(f.path)
          return (
            <li
              key={f.path}
              data-testid="skill-file-row"
              className={`flex items-center justify-between gap-2 rounded px-1 py-0.5 ${isMarked ? 'bg-red-50' : ''}`}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <code className={`truncate font-mono ${isMarked ? 'text-red-700 line-through' : 'text-gray-700'}`}>
                  {relative}
                </code>
                {isSkillMd && <span className="shrink-0 text-[10px] text-gray-400">the skill itself</span>}
                {f.core_owned && !isSkillMd && (
                  <span className="shrink-0 text-[10px] italic text-gray-500">synced from core · read-only</span>
                )}
                {staged.has(f.path) && <StagedBadge />}
              </span>
              {canDelete && !isSkillMd && !f.core_owned && (
                isMarked ? (
                  <button
                    type="button"
                    data-testid={`unmark-delete-${f.path}`}
                    disabled={disabled}
                    onClick={() => toggle(f.path)}
                    className="shrink-0 text-[11px] text-gray-600 hover:underline disabled:opacity-50"
                  >
                    keep
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid={`mark-delete-${f.path}`}
                    disabled={disabled}
                    onClick={() => toggle(f.path)}
                    className="shrink-0 text-[11px] text-red-600 hover:underline disabled:opacity-50"
                  >
                    remove
                  </button>
                )
              )}
            </li>
          )
        })}
      </ul>

      {marked.length > 0 && (
        <div data-testid="skill-files-pending" className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-red-800">
          <p className="font-medium">
            {marked.length === 1 ? '1 file will be removed' : `${marked.length} files will be removed`} when you propose
            this change. Nothing is deleted until you approve the pull request.
          </p>
          <ul className="mt-1 space-y-0.5">
            {marked.map(p => (
              <li key={p}>
                <code className="font-mono">{p.startsWith(dir) ? p.slice(dir.length) : p}</code>
              </li>
            ))}
          </ul>
        </div>
      )}

      {dangling.length > 0 && (
        <div data-testid="skill-files-dangling" className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-800">
          <p className="font-medium">SKILL.md still points at a file you are removing.</p>
          <ul className="mt-1 space-y-0.5">
            {dangling.map(d => (
              <li key={d.path}>
                <code className="font-mono">{d.path}</code> — line {d.line}: <span className="italic">{d.text}</span>
              </li>
            ))}
          </ul>
          <p className="mt-1">
            If you are dropping the reference in this same change, this is already handled — it is checked against your
            current draft.
          </p>
        </div>
      )}
    </div>
  )
}
